// Admin API client

const BASE = '/api/admin';

function getToken() {
  return localStorage.getItem('am_admin_token');
}

async function request(method, path, body) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401) {
    localStorage.removeItem('am_admin_token');
    window.location.href = '/admin/login';
    return null;
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
  return data;
}

export const adminApi = {
  login: (email, password) =>
    request('POST', '/auth/login', { email, password }),

  me: () => request('GET', '/auth/me'),

  stats: () => request('GET', '/stats'),

  listUsers: () => request('GET', '/users'),

  getUser: (id) => request('GET', `/users/${id}`),

  suspendUser: (id) => request('PATCH', `/users/${id}/suspend`),

  // #228 — permanent delete (different from /reset which keeps the
  // family_users row). Used by AdminUserDetail's "Delete account" button.
  deleteUser: (id) => request('DELETE', `/users/${id}`),

  usage: () => request('GET', '/usage'),

  listConversations: () => request('GET', '/conversations'),

  getConversation: (id) => request('GET', `/conversations/${id}`),

  // Engagement timeline for a single conversation (#151)
  getConversationEvents: (id) => request('GET', `/conversations/${id}/events`),

  // Per-turn Call Diagnostics for a single conversation (#702)
  getConversationDiagnostics: (id) =>
    request('GET', `/conversations/${id}/diagnostics`),

  // Per-user opt-in mic-audio capture (#597)
  getAudioRecordingFlag: (userId) =>
    request('GET', `/users/${userId}/audio-recording`),
  setAudioRecordingFlag: (userId, enabled) =>
    request('PUT', `/users/${userId}/audio-recording`, { enabled }),

  // Per-user conversation-steering mode (#981) — 'suggested' | 'guided'.
  // Current value is returned by getUser(); this sets it. Admin-only A/B
  // knob flipped from the Cards sub-page.
  setConversationSteering: (userId, steering) =>
    request('POST', `/users/${userId}/conversation-steering`, { steering }),

  // Per-conversation list of opt-in mic-audio clips (#597)
  listVoiceAudioClips: (conversationId) =>
    request(
      'GET',
      `/voice-audio?conversation_id=${encodeURIComponent(conversationId)}`,
    ),

  systemHealth: () => request('GET', '/system/health'),

  systemLogs: ({ limit = 200, level = '', showPolling = false } = {}) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (level) params.set('level', level);
    if (showPolling) params.set('show_polling', 'true');
    return request('GET', `/system/logs?${params}`);
  },

  analytics: () => request('GET', '/analytics'),

  getPublicConfig: async () => {
    const res = await fetch('/api/config/public');
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
    return data;
  },

  updateConfig: (body) => request('PUT', '/config', body),

  securityEvents: (type, limit = 50) => {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    params.set('limit', String(limit));
    return request('GET', `/security-events?${params}`);
  },
};

export function isAdminLoggedIn() {
  return Boolean(localStorage.getItem('am_admin_token'));
}

export const adminGet = (path) => request('GET', path);
export const adminPut = (path, body) => request('PUT', path, body);
export const adminPost = (path, body) => request('POST', path, body);
export const adminPatch = (path, body) => request('PATCH', path, body);
export const adminDelete = (path) => request('DELETE', path);
